/**
 * useChat — Master chat orchestration hook.
 *
 * Owns:
 *   • Cloud/localStorage chat loading
 *   • Active-chat persistence
 *   • All message-flow logic (send/regenerate/edit/feedback)
 *   • Artifact / report panel handlers
 *   • Document / drafting / analysis polling orchestration
 *
 * Returns the SAME public API previously exposed by ChatContext.jsx so
 * downstream consumers (`useChat()`) keep working unchanged.
 */
import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';
import useChatStore, { ACTIVE_CHAT_STORAGE_KEY } from '@/stores/chatStore';
import { useStreaming } from '@/hooks/useStreaming';
import { useDrafting } from '@/hooks/useDrafting';
import {
  MAX_CHATS,
  DRAFTING_TIMEOUT_MS,
  ERROR_MESSAGES,
  DOCUMENT_ERROR_MESSAGES,
  REQUEST_TIMEOUT_MESSAGES,
  AI_TIMEOUT_MESSAGES,
  DELAY_TIMEOUT_MESSAGES,
  PDF_TIMEOUT_MESSAGES,
  PROGRESS_MESSAGES,
  DEFAULT_DOCUMENT_PROMPTS,
  ANALYSIS_PROGRESS_MESSAGES,
  ANALYSIS_STEPS,
  STAGE_TO_STEP_INDEX,
  ARTIFACT_CONFIRMATION_MESSAGES,
} from '@/lib/chatConstants';
import {
  generateId,
  isArabicText,
  sanitizeText,
  sanitizeTitle,
  removeInvisibleDraftWarmupChars,
  extractSourcesFromResponse,
  extractSuggestionFromResponse,
  extractLawSystemsFromSources,
  detectModeForTracking,
  hasDocumentReviewIntent,
  looksLikeLongLegalDraft,
  buildApiMessages,
} from '@/lib/chatHelpers';

const BACKEND_URL = getBackendBaseUrl();
const API_URL = `${BACKEND_URL}/api`;

export const useChatLogic = () => {
  const { isAuthenticated, getAccessToken, session, trackQuestion, loading: authLoading } = useAuth();
  const { language: uiLanguage } = useLanguage();

  // ── Zustand store bindings ───────────────────────────────────
  const chats = useChatStore((s) => s.chats);
  const setChats = useChatStore((s) => s.setChats);
  const chatsInitialized = useChatStore((s) => s.chatsInitialized);
  const setChatsInitialized = useChatStore((s) => s.setChatsInitialized);
  const currentChatId = useChatStore((s) => s.currentChatId);
  const setCurrentChatId = useChatStore((s) => s.setCurrentChatId);
  const isTyping = useChatStore((s) => s.isTyping);
  const setIsTyping = useChatStore((s) => s.setIsTyping);
  const typingLabel = useChatStore((s) => s.typingLabel);
  const setTypingLabel = useChatStore((s) => s.setTypingLabel);
  const typingMode = useChatStore((s) => s.typingMode);
  const setTypingMode = useChatStore((s) => s.setTypingMode);
  const analysisStepIndex = useChatStore((s) => s.analysisStepIndex);
  const setAnalysisStepIndex = useChatStore((s) => s.setAnalysisStepIndex);
  const documentContexts = useChatStore((s) => s.documentContexts);
  const setDocumentContexts = useChatStore((s) => s.setDocumentContexts);
  const pendingDocument = useChatStore((s) => s.pendingDocument);
  const setPendingDocument = useChatStore((s) => s.setPendingDocument);
  const draftModeByChatId = useChatStore((s) => s.draftModeByChatId);
  const setDraftModeByChatId = useChatStore((s) => s.setDraftModeByChatId);
  const artifactPanel = useChatStore((s) => s.artifactPanel);
  const setArtifactPanel = useChatStore((s) => s.setArtifactPanel);
  const setArtifactByMessageId = useChatStore((s) => s.setArtifactByMessageId);
  const composerPrefill = useChatStore((s) => s.composerPrefill);
  const setComposerPrefill = useChatStore((s) => s.setComposerPrefill);
  const quotedMessage = useChatStore((s) => s.quotedMessage);
  const setQuotedMessage = useChatStore((s) => s.setQuotedMessage);
  const isLoadingChats = useChatStore((s) => s.isLoadingChats);
  const setIsLoadingChats = useChatStore((s) => s.setIsLoadingChats);
  const reportPanel = useChatStore((s) => s.reportPanel);
  const setReportPanel = useChatStore((s) => s.setReportPanel);
  const totalMessageCount = useChatStore((s) => s.totalMessageCount);
  const setTotalMessageCount = useChatStore((s) => s.setTotalMessageCount);
  const incrementMessageCount = useChatStore((s) => s.incrementMessageCount);

  // ── Refs ─────────────────────────────────────────────────────
  const chatsSourceRef = useRef('none');
  const initialRestoreDoneRef = useRef(false);
  const abortControllerRef = useRef(null);
  const abortReasonRef = useRef(null);
  const progressTimersRef = useRef([]);
  const firstTokenReceivedRef = useRef(false);

  // ── Sub-hooks ────────────────────────────────────────────────
  const { callChatAPI, streamChatAPI, pollExistingAnalysisJob } = useStreaming();
  const { callDraftAPI } = useDrafting();

  // Derived
  const currentChat = chats.find((chat) => chat.id === currentChatId);
  const currentDocument = currentChatId ? documentContexts[currentChatId] || null : null;

  // ── Track usage event ───────────────────────────────────────
  const trackUsageEvent = useCallback(async (eventType, metadata) => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      await fetch(`${API_URL}/track/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ event_type: eventType, metadata: metadata || {} }),
      });
    } catch (err) {
      console.error('Failed to track event', err);
    }
  }, [getAccessToken]);

  // ── Cloud sync ──────────────────────────────────────────────
  // Cluster B-3.b: route the chat upsert through apiFetch so a 401
  // here also surfaces `ReauthBanner` (the user signed out between
  // mount and the next chat mutation). The Authorization header is
  // added by apiFetch from the current session.
  const syncChatToCloud = useCallback(async (chat) => {
    if (!isAuthenticated || !session?.access_token) return;
    try {
      const { apiFetch } = await import('@/lib/apiFetch');
      await apiFetch(`${API_URL}/auth/chats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: chat.id,
          title: chat.title,
          messages: chat.messages,
          conversationId: chat.conversationId,
          createdAt: chat.createdAt,
          updatedAt: chat.updatedAt,
        }),
      });
    } catch (error) {
      console.error('Error syncing chat to cloud:', error);
    }
  }, [isAuthenticated, session?.access_token]);

  // ── Pending document on chat switch ─────────────────────────
  useEffect(() => {
    setPendingDocument(null);
  }, [currentChatId, setPendingDocument]);

  // ── Persist active chat id ──────────────────────────────────
  useEffect(() => {
    if (!initialRestoreDoneRef.current) return;
    if (currentChatId) {
      localStorage.setItem(ACTIVE_CHAT_STORAGE_KEY, currentChatId);
    } else {
      localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
    }
  }, [currentChatId]);

  // ── Load chats on mount / auth change ───────────────────────
  useEffect(() => {
    if (authLoading) return;

    const loadChats = async () => {
      setIsLoadingChats(true);

      if (isAuthenticated && session?.access_token) {
        try {
          const response = await axios.get(`${API_URL}/auth/chats`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (response.data?.chats) {
            const cloudChats = response.data.chats
              .map((chat) => ({
                id: chat.local_id || chat.id || chat.session_id || chat._id,
                title: chat.title,
                messages: chat.messages || [],
                conversationId: chat.conversation_id,
                createdAt: chat.created_at,
                updatedAt: chat.updated_at,
              }))
              .filter((chat) => Boolean(chat.id));
            chatsSourceRef.current = 'cloud';
            setChats(cloudChats);
            console.log('[CHAT] Loaded', cloudChats.length, 'chats from cloud');
          }
        } catch (error) {
          console.error('Error loading cloud chats:', error);
          chatsSourceRef.current = 'cloud';
          setChats([]);
        }
      } else {
        const saved = localStorage.getItem('legal-advisor-chats');
        if (saved) {
          try {
            chatsSourceRef.current = 'guest';
            setChats(JSON.parse(saved));
          } catch {
            chatsSourceRef.current = 'guest';
          }
        } else {
          chatsSourceRef.current = 'guest';
        }
      }

      setIsLoadingChats(false);
      setChatsInitialized(true);
    };

    loadChats();
    // Cluster G.C (Feb 2026): `loadChats` is an inline function
    // closure that captures the latest store setters via Zustand.
    // Including it in deps would force the effect to re-run every
    // render (the closure identity changes each pass). The current
    // deps — auth state — are the only triggers that should cause a
    // re-load. Intentional omission.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, session?.access_token]);

  // ── Restore active chat on refresh ──────────────────────────
  useEffect(() => {
    if (!chatsInitialized) return;

    if (currentChatId && chats.some((chat) => chat.id === currentChatId)) {
      initialRestoreDoneRef.current = true;
      return;
    }

    // Cluster A: cross-device restore via /api/auth/profile/me with a
    // 24-hour freshness window. `window.__BAYENAH_RESTORE_ACTIVE_CHAT`
    // is populated once-per-session by AuthContext right after the
    // profile fetch on SIGNED_IN. We honour it ahead of localStorage
    // so a fresh device picks up where the user left off; if it's
    // older than 24h it's ignored and we fall back to the local hint.
    const restoreHint = (typeof window !== 'undefined')
      ? window.__BAYENAH_RESTORE_ACTIVE_CHAT
      : null;
    if (restoreHint?.chatId && chats.some((chat) => chat.id === restoreHint.chatId)) {
      const ageMs = restoreHint.atMs ? (Date.now() - restoreHint.atMs) : Infinity;
      if (ageMs <= 24 * 60 * 60 * 1000) {
        setCurrentChatId(restoreHint.chatId);
        initialRestoreDoneRef.current = true;
        try { window.__BAYENAH_RESTORE_ACTIVE_CHAT = null; } catch { /* ignore */ }
        return;
      }
      // Stale — clear the hint and fall through to localStorage.
      try { window.__BAYENAH_RESTORE_ACTIVE_CHAT = null; } catch { /* ignore */ }
    }

    const savedActiveChatId = localStorage.getItem(ACTIVE_CHAT_STORAGE_KEY);
    if (savedActiveChatId && chats.some((chat) => chat.id === savedActiveChatId)) {
      setCurrentChatId(savedActiveChatId);
      initialRestoreDoneRef.current = true;
      return;
    }

    initialRestoreDoneRef.current = true;

    if (currentChatId && !chats.some((chat) => chat.id === currentChatId)) {
      setCurrentChatId(null);
      localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
    }
    // Cluster G.C (Feb 2026): the `setCurrentChatId` setter is a
    // stable Zustand action and is intentionally excluded — adding
    // it would not change semantics but eslint can't statically
    // prove that. Keep effect dep list minimal to avoid spurious
    // re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatsInitialized, chats, currentChatId]);

  // Cluster A: write-through active_chat_id to the server profile so
  // the user's most recent matter follows them across devices. We
  // POST `/api/auth/profile/sync` whenever the local currentChatId
  // changes; the server stamps `active_chat_at` as `now` and the
  // 24h freshness window is enforced on read (above).
  //
  // GUARD: only POST when `currentChatId` is a real id. Posting an
  // empty string when the user has no active chat would clobber a
  // valid `active_chat_id` written from another device.
  useEffect(() => {
    if (!isAuthenticated || !session?.access_token) return;
    if (!chatsInitialized) return;
    if (!currentChatId) return;
    let cancelled = false;
    const post = async () => {
      try {
        const { apiFetch } = await import('@/lib/apiFetch');
        await apiFetch(`${BACKEND_URL}/api/auth/profile/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ active_chat_id: currentChatId }),
        });
      } catch (err) {
        if (!cancelled) console.warn('[active-chat-sync] failed', err?.message || err);
      }
    };
    post();
    return () => { cancelled = true; };
  }, [currentChatId, isAuthenticated, session?.access_token, chatsInitialized]);

  // ── Save chats to localStorage for guests only ──────────────
  useEffect(() => {
    if (!chatsInitialized) return;
    if (!isAuthenticated && chatsSourceRef.current === 'guest') {
      localStorage.setItem('legal-advisor-chats', JSON.stringify(chats.slice(0, MAX_CHATS)));
    }
  }, [chats, isAuthenticated, chatsInitialized]);

  // ── Conversation handlers ───────────────────────────────────
  const closeArtifactPanel = useCallback(() => {
    setArtifactPanel((prev) => ({ ...prev, isOpen: false }));
  }, [setArtifactPanel]);

  const openArtifactPanel = useCallback((artifact) => {
    if (!artifact?.content) return;
    setArtifactPanel({
      isOpen: true,
      type: artifact.type || 'draft',
      title: artifact.title || (uiLanguage === 'ar' ? 'مستند قانوني' : 'Legal Document'),
      content: artifact.content,
      messageId: artifact.messageId || null,
      conversationId: artifact.conversationId || null,
      sourcePrompt: artifact.sourcePrompt || '',
      sourceLanguage: artifact.sourceLanguage || uiLanguage,
    });
  }, [uiLanguage, setArtifactPanel]);

  const createNewChat = useCallback(() => {
    setCurrentChatId(null);
    setQuotedMessage(null);
    setPendingDocument(null);
    setDraftModeByChatId({});
    setArtifactPanel((prev) => ({ ...prev, isOpen: false }));
    localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
  }, [setCurrentChatId, setQuotedMessage, setPendingDocument, setDraftModeByChatId, setArtifactPanel]);

  const createChatEntry = useCallback(() => {
    const newChat = {
      id: generateId(),
      title: '',
      messages: [],
      conversationId: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setChats((prev) => [newChat, ...prev].slice(0, MAX_CHATS));
    setCurrentChatId(newChat.id);
    return newChat.id;
  }, [setChats, setCurrentChatId]);

  const deleteChat = useCallback(async (chatId) => {
    if (isAuthenticated && session?.access_token) {
      try {
        await axios.delete(`${API_URL}/auth/chats/${chatId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
      } catch (error) {
        console.error('Error deleting chat from cloud:', error);
      }
    }
    setChats((prev) => prev.filter((chat) => chat.id !== chatId));
    setDocumentContexts((prev) => {
      const updated = { ...prev };
      delete updated[chatId];
      return updated;
    });
    setDraftModeByChatId((prev) => {
      const updated = { ...prev };
      delete updated[chatId];
      return updated;
    });
    if (currentChatId === chatId) {
      setCurrentChatId(null);
      closeArtifactPanel();
      localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
    }
  }, [currentChatId, isAuthenticated, session, closeArtifactPanel, setChats, setDocumentContexts, setDraftModeByChatId, setCurrentChatId]);

  const selectChat = useCallback((chatId) => {
    setCurrentChatId(chatId);
    setPendingDocument(null);
    closeArtifactPanel();
  }, [closeArtifactPanel, setCurrentChatId, setPendingDocument]);

  const renameChat = useCallback((chatId, newTitle) => {
    setChats((prev) => prev.map((chat) => {
      if (chat.id === chatId) {
        const updatedChat = { ...chat, title: newTitle, updatedAt: new Date().toISOString() };
        syncChatToCloud(updatedChat);
        return updatedChat;
      }
      return chat;
    }));
  }, [setChats, syncChatToCloud]);

  const clearCurrentChat = useCallback(() => {
    if (currentChatId) {
      setDraftModeByChatId((prev) => {
        const next = { ...prev };
        delete next[currentChatId];
        return next;
      });
    }
    setCurrentChatId(null);
    setQuotedMessage(null);
    setPendingDocument(null);
    closeArtifactPanel();
    localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
  }, [closeArtifactPanel, currentChatId, setDraftModeByChatId, setCurrentChatId, setQuotedMessage, setPendingDocument]);

  const clearAllChatData = useCallback(() => {
    console.log('[Bayenah] Clearing all chat data for logout');
    setChats([]);
    setCurrentChatId(null);
    setQuotedMessage(null);
    setDocumentContexts({});
    setPendingDocument(null);
    setTotalMessageCount(0);
    setDraftModeByChatId({});
    closeArtifactPanel();
    localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
    localStorage.removeItem('legal-advisor-chats');
    localStorage.removeItem('legal-advisor-message-count');
    localStorage.removeItem('bayenah-message-feedback');
    console.log('[Bayenah] Chat data cleared successfully');
  }, [closeArtifactPanel, setChats, setCurrentChatId, setQuotedMessage, setDocumentContexts, setPendingDocument, setTotalMessageCount, setDraftModeByChatId]);

  // ── Document / pending handlers ─────────────────────────────
  const attachDocument = useCallback((doc) => {
    if (!doc) {
      setPendingDocument(null);
      return;
    }
    setPendingDocument({ ...doc, text: sanitizeText(doc.text || '') });
  }, [setPendingDocument]);

  const clearPendingDocument = useCallback(() => {
    setPendingDocument(null);
  }, [setPendingDocument]);

  // ── Artifact / panel handlers ───────────────────────────────
  const openArtifactFromMessage = useCallback((messageId) => {
    const stored = useChatStore.getState().artifactByMessageId;
    const artifact = stored[messageId];
    if (artifact) {
      openArtifactPanel(artifact);
      return;
    }

    const chat = chats.find((c) => c.id === currentChatId);
    if (!chat) return;

    const message = chat.messages.find((m) => m.id === messageId);
    if (!message?.meta?.artifact_panel) return;

    const meta = message.meta;
    const panelMeta = meta.artifact_panel;
    const draftJobId = meta.draft_job?.draft_id || meta.analysis_job?.job_id;

    if (draftJobId) {
      const fetchUrl = meta.type === 'contract_draft' || panelMeta.type === 'draft'
        ? `${BACKEND_URL}/api/draft-status/${draftJobId}`
        : `${BACKEND_URL}/api/analysis-jobs/${draftJobId}`;

      fetch(fetchUrl)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data) return;
          const content = data.draft_text || data.final_result?.review_text || '';
          if (!content) return;

          const reconstructed = {
            type: panelMeta.type || 'draft',
            title: panelMeta.title || meta.artifact_title || 'مسودة عقد',
            content,
            messageId,
            conversationId: chat.conversationId,
            sourcePrompt: '',
            sourceLanguage: meta.language || 'ar',
          };
          setArtifactByMessageId((prev) => ({ ...prev, [messageId]: reconstructed }));
          openArtifactPanel(reconstructed);
        })
        .catch(() => {});
    }
  }, [chats, currentChatId, openArtifactPanel, setArtifactByMessageId]);

  const requestArtifactEdit = useCallback(() => {
    const ap = useChatStore.getState().artifactPanel;
    if (!ap?.content) return;
    const prefill = ap.sourceLanguage === 'ar'
      ? 'عدّل المسودة الحالية مع الحفاظ على الأسلوب القانوني السعودي، مع التركيز على: '
      : 'Please revise the current draft while preserving Saudi legal style, focusing on: ';
    setComposerPrefill(prefill);
    setArtifactPanel((prev) => ({ ...prev, isOpen: false }));
  }, [setComposerPrefill, setArtifactPanel]);

  const consumeComposerPrefill = useCallback(() => {
    setComposerPrefill('');
  }, [setComposerPrefill]);

  // ── Quote / report panel ────────────────────────────────────
  const quoteMessage = useCallback((message) => setQuotedMessage(message), [setQuotedMessage]);
  const clearQuote = useCallback(() => setQuotedMessage(null), [setQuotedMessage]);
  const openReportPanel = useCallback((report, messageId) => {
    setReportPanel({ isOpen: true, report, messageId });
  }, [setReportPanel]);
  const closeReportPanel = useCallback(() => {
    setReportPanel({ isOpen: false, report: null, messageId: null });
  }, [setReportPanel]);

  // ── Progress timers ─────────────────────────────────────────
  const clearProgressTimers = useCallback(() => {
    progressTimersRef.current.forEach((timer) => clearTimeout(timer));
    progressTimersRef.current = [];
  }, []);

  const scheduleProgressTimers = useCallback((lang, onTimeout, mode = 'question') => {
    clearProgressTimers();
    if (mode === 'drafting') {
      setTypingLabel(lang === 'ar' ? 'جاري إعداد المسودة القانونية…' : 'Preparing legal draft…');
      return;
    }
    const messages = PROGRESS_MESSAGES[lang] || PROGRESS_MESSAGES.en;
    setTypingLabel(messages.stage1);
    progressTimersRef.current.push(setTimeout(() => setTypingLabel(messages.stage2), 2000));
    progressTimersRef.current.push(setTimeout(() => setTypingLabel(messages.stage3), 5000));
    progressTimersRef.current.push(setTimeout(() => setTypingLabel(messages.long), 15000));
    progressTimersRef.current.push(setTimeout(() => onTimeout('delay'), 30000));
    progressTimersRef.current.push(setTimeout(() => onTimeout('max'), 45000));
  }, [clearProgressTimers, setTypingLabel]);

  const resolveErrorMessage = useCallback((error, lang, usesDocument, fallbackMessage = '') => {
    const raw = (error?.message || '').toString();
    const normalized = raw.toLowerCase();
    if (normalized.includes('request_timeout')) return REQUEST_TIMEOUT_MESSAGES[lang] || REQUEST_TIMEOUT_MESSAGES.en;
    if (normalized.includes('ai_timeout')) return AI_TIMEOUT_MESSAGES[lang] || AI_TIMEOUT_MESSAGES.en;
    if (normalized.includes('pdf_timeout')) return PDF_TIMEOUT_MESSAGES[lang] || PDF_TIMEOUT_MESSAGES.en;
    if (normalized.includes('delay_timeout')) return DELAY_TIMEOUT_MESSAGES[lang] || DELAY_TIMEOUT_MESSAGES.en;
    if (fallbackMessage) return fallbackMessage;
    return usesDocument
      ? (DOCUMENT_ERROR_MESSAGES[lang] || DOCUMENT_ERROR_MESSAGES.en)
      : (ERROR_MESSAGES[lang] || ERROR_MESSAGES.en);
  }, []);

  // ── Stop generating ─────────────────────────────────────────
  const stopGenerating = useCallback(() => {
    if (abortControllerRef.current) {
      abortReasonRef.current = 'user';
      abortControllerRef.current.abort();
      clearProgressTimers();
      setIsTyping(false);
      setTypingLabel(null);
    }
  }, [clearProgressTimers, setIsTyping, setTypingLabel]);

  // ── Send message (the heavy lift) ───────────────────────────
  const sendMessage = useCallback(async (content, replyToId = null, messageOptions = {}) => {
    let chatId = currentChatId;
    let chatConversationId = null;
    const activePendingDocument = pendingDocument;
    const storedDocumentContext = documentContexts[chatId];
    const activeDocument = activePendingDocument || storedDocumentContext;
    const hasAnyDocument = Boolean(activeDocument);
    const trimmedDisplayContent = (messageOptions.displayContent || content || '').trim();
    const trimmedApiContent = (messageOptions.apiContent || content || '').trim();
    const defaultPromptLanguage = activeDocument?.language || (isArabicText(trimmedDisplayContent || trimmedApiContent) ? 'ar' : 'en');
    const effectiveContent = trimmedApiContent || (hasAnyDocument ? DEFAULT_DOCUMENT_PROMPTS[defaultPromptLanguage] : '');
    const displayContent = trimmedDisplayContent || effectiveContent;

    if (!effectiveContent) return;

    if (!chatId) chatId = createChatEntry();

    const existingChat = chats.find((c) => c.id === chatId);
    chatConversationId = existingChat?.conversationId || null;

    const userMessage = {
      id: generateId(),
      role: 'user',
      content: displayContent,
      timestamp: new Date().toISOString(),
      replyTo: replyToId,
      ...(activePendingDocument
        ? { file: { name: activePendingDocument.fileName, size: activePendingDocument.fileSize, status: 'uploaded' } }
        : {}),
    };

    const aiMessageId = generateId();

    setChats((prev) => prev.map((chat) => {
      if (chat.id === chatId) {
        const isFirstMessage = chat.messages.length === 0;
        return {
          ...chat,
          title: isFirstMessage
            ? sanitizeTitle(displayContent, { lang: uiLanguage, maxLen: 50 })
            : chat.title,
          messages: [
            ...chat.messages,
            userMessage,
            { id: aiMessageId, role: 'assistant', content: '', timestamp: new Date().toISOString(), feedback: null, isStreaming: true },
          ],
          updatedAt: new Date().toISOString(),
        };
      }
      return chat;
    }));

    // Cluster A checkpoint (1/3): user-message landed.
    // Persist the chat as soon as the user message is committed so a
    // mid-flow signout/refresh never loses the prompt itself.
    try {
      const justUpdated = useChatStore.getState().chats.find((c) => c.id === chatId);
      if (justUpdated) syncChatToCloud(justUpdated);
    } catch (err) {
      console.warn('[checkpoint:user-msg] sync skipped', err?.message || err);
    }

    if (activePendingDocument) {
      setDocumentContexts((prev) => ({
        ...prev,
        [chatId]: {
          text: activePendingDocument.text,
          name: activePendingDocument.fileName,
          size: activePendingDocument.fileSize,
          truncated: activePendingDocument.truncated,
          language: activePendingDocument.language,
        },
      }));
      setPendingDocument(null);
    }

    incrementMessageCount();
    setQuotedMessage(null);

    const existingMessages = existingChat?.messages || [];

    const hasExplicitReviewIntent = hasDocumentReviewIntent(effectiveContent);
    void hasExplicitReviewIntent;
    const usesDocument = hasAnyDocument;
    const isDocumentReviewFlow = usesDocument;
    const enforceClientTimeouts = !isDocumentReviewFlow;
    let isDraftingFlow = false;

    const requestStart = Date.now();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    abortReasonRef.current = null;
    firstTokenReceivedRef.current = false;

    const handleTimeout = (type) => {
      if (!enforceClientTimeouts) return;
      if (firstTokenReceivedRef.current || !abortControllerRef.current) return;
      abortReasonRef.current = type === 'delay' ? 'timeout-delay' : 'timeout-max';
      abortControllerRef.current.abort();
    };

    const typingUiMode = isDraftingFlow ? 'drafting' : (usesDocument ? 'document' : 'question');
    scheduleProgressTimers(uiLanguage, handleTimeout, typingUiMode);
    setTypingMode(typingUiMode);
    setIsTyping(true);

    const apiMessages = usesDocument ? [] : buildApiMessages([...existingMessages, userMessage]);

    const documentPayload = usesDocument
      ? { text: activeDocument.text, question: effectiveContent, language: defaultPromptLanguage }
      : null;

    const onChunk = (partialText) => {
      const isFirstToken = !firstTokenReceivedRef.current;
      if (isFirstToken) {
        firstTokenReceivedRef.current = true;
        clearProgressTimers();
        setTypingLabel(null);
      }
      setChats((prev) => prev.map((chat) => {
        if (chat.id === chatId) {
          return {
            ...chat,
            messages: chat.messages.map((msg) =>
              msg.id === aiMessageId ? { ...msg, content: partialText, isStreaming: true, isError: false } : msg
            ),
            updatedAt: new Date().toISOString(),
          };
        }
        return chat;
      }));
      // Cluster A checkpoint (2/3): first assistant token has arrived.
      // Persist once so the user sees a partial draft after a mid-flow
      // signout instead of losing the response altogether.
      if (isFirstToken) {
        try {
          const checkpointChat = useChatStore.getState().chats.find((c) => c.id === chatId);
          if (checkpointChat) syncChatToCloud(checkpointChat);
        } catch (err) {
          console.warn('[checkpoint:first-token] sync skipped', err?.message || err);
        }
      }
    };

    const onMeta = (meta) => {
      if (!meta) return;
      setChats((prev) => prev.map((chat) => {
        if (chat.id === chatId) {
          return {
            ...chat,
            messages: chat.messages.map((msg) => (msg.id === aiMessageId ? { ...msg, meta } : msg)),
            updatedAt: new Date().toISOString(),
          };
        }
        return chat;
      }));
    };

    try {
      let response;
      let lastError;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          if (attempt > 0) {
            setChats((prev) => prev.map((chat) => {
              if (chat.id === chatId) {
                return {
                  ...chat,
                  messages: chat.messages.map((msg) =>
                    msg.id === aiMessageId ? { ...msg, content: '', isStreaming: true, isError: false } : msg
                  ),
                };
              }
              return chat;
            }));
          }
          response = await streamChatAPI(apiMessages, chatConversationId, abortController.signal, onChunk, documentPayload, onMeta);
          lastError = null;
          break;
        } catch (err) {
          if (err.name === 'AbortError' || err.message === 'Aborted') throw err;
          lastError = err;
        }
      }

      if (!response) throw lastError || new Error('Service temporarily unavailable');

      // ── Async Analysis Job ─────────────────────────────────
      if (response.meta?.type === 'analysis_job_started' && response.meta?.analysis_job?.job_id) {
        const analysisJobId = response.meta.analysis_job.job_id;

        setAnalysisStepIndex(0);
        setTypingLabel(ANALYSIS_PROGRESS_MESSAGES.pending[defaultPromptLanguage] || ANALYSIS_PROGRESS_MESSAGES.pending.ar);

        setChats((prev) => prev.map((chat) => {
          if (chat.id === chatId) {
            return {
              ...chat,
              messages: chat.messages.map((msg) =>
                msg.id === aiMessageId
                  ? {
                      ...msg,
                      content: response.text || (defaultPromptLanguage === 'ar' ? '🔍 جاري تحليل المستند...' : '🔍 Analyzing document...'),
                      isStreaming: true,
                      isError: false,
                      meta: { ...response.meta, analysisJobId },
                    }
                  : msg
              ),
            };
          }
          return chat;
        }));

        const onAnalysisProgress = (progressMsg, stage) => {
          setTypingLabel(progressMsg);
          const stepIndex = STAGE_TO_STEP_INDEX[stage] ?? 0;
          setAnalysisStepIndex(stepIndex);
        };

        const onPartialResult = (partialData) => {
          if (!partialData?.partialResult) return;
          const partialText = partialData.partialResult.phase1_text
            || partialData.partialResult.summary
            || (defaultPromptLanguage === 'ar' ? 'جاري إكمال التحليل...' : 'Completing analysis...');
          setChats((prev) => prev.map((chat) => {
            if (chat.id === chatId) {
              return {
                ...chat,
                messages: chat.messages.map((msg) =>
                  msg.id === aiMessageId
                    ? {
                        ...msg,
                        content: partialText,
                        isStreaming: true,
                        meta: { ...msg.meta, partial_result: partialData.partialResult, analysis_status: partialData.status },
                      }
                    : msg
                ),
              };
            }
            return chat;
          }));
        };

        try {
          const analysisResponse = await pollExistingAnalysisJob(
            analysisJobId,
            response.conversationId || chatConversationId,
            abortController.signal,
            onAnalysisProgress,
            onPartialResult
          );

          response = {
            text: analysisResponse.text,
            conversationId: analysisResponse.conversationId || response.conversationId,
            meta: {
              ...response.meta,
              ...analysisResponse.meta,
              review_scorecard: analysisResponse.meta?.review_scorecard,
            },
          };

          clearProgressTimers();
          setTypingLabel(null);
          setAnalysisStepIndex(0);
          firstTokenReceivedRef.current = true;
        } catch (analysisError) {
          console.error('[ANALYSIS_JOB] Polling failed:', analysisError);
          clearProgressTimers();
          setTypingLabel(null);
          setAnalysisStepIndex(0);
          if (analysisError.message === 'REQUEST_TIMEOUT' || analysisError.message === 'Aborted') {
            response = {
              text: defaultPromptLanguage === 'ar'
                ? '⏳ التحليل يستغرق وقتاً أطول من المتوقع. يرجى الانتظار أو المحاولة مرة أخرى.'
                : '⏳ Analysis is taking longer than expected. Please wait or try again.',
              conversationId: response.conversationId,
              meta: { ...response.meta, analysis_timeout: true },
            };
          } else {
            throw analysisError;
          }
        }
      }

      // ── Async Draft Job ────────────────────────────────────
      if (response.meta?.type === 'draft_job_started' && response.meta?.draft_job?.draft_id) {
        const draftJobId = response.meta.draft_job.draft_id;
        isDraftingFlow = true;
        setTypingMode('drafting');
        setTypingLabel(uiLanguage === 'ar' ? 'جاري إعداد المسودة...' : 'Preparing draft...');
        setIsTyping(true);

        setChats((prev) => prev.map((chat) => {
          if (chat.id === chatId) {
            return {
              ...chat,
              messages: chat.messages.map((msg) =>
                msg.id === aiMessageId
                  ? { ...msg, content: response.text || '', isStreaming: true, isError: false, meta: response.meta }
                  : msg
              ),
            };
          }
          return chat;
        }));

        try {
          const pollInterval = 3000;
          const pollMaxDuration = 240000;
          const pollStart = Date.now();

          while (Date.now() - pollStart < pollMaxDuration) {
            await new Promise((r) => setTimeout(r, pollInterval));
            if (abortController.signal.aborted) throw new Error('Aborted');

            const statusResp = await axios.get(`${BACKEND_URL}/api/draft-status/${draftJobId}`, {
              signal: abortController.signal,
            });
            const job = statusResp.data;

            if (job.status === 'done' && job.draft_text) {
              response = {
                text: job.draft_text,
                conversationId: job.conversation_id || response.conversationId,
                meta: {
                  ...response.meta,
                  type: 'contract_draft',
                  artifact_title: job.artifact?.title,
                  contract_type: job.artifact?.contract_type,
                  word_count: job.artifact?.word_count,
                },
              };
              break;
            }

            if (job.status === 'failed') {
              throw new Error(job.error_message || (uiLanguage === 'ar' ? 'تعذر إنشاء المسودة' : 'Draft generation failed'));
            }

            if (job.stage) {
              const stageLabels = {
                queued: uiLanguage === 'ar' ? 'في قائمة الانتظار...' : 'Queued...',
                detecting_type: uiLanguage === 'ar' ? 'جاري تحديد نوع العقد...' : 'Detecting contract type...',
                generating_clauses: uiLanguage === 'ar' ? 'جاري صياغة المسودة...' : 'Generating draft...',
                finalizing: uiLanguage === 'ar' ? 'جاري الإنهاء...' : 'Finalizing...',
                completed: uiLanguage === 'ar' ? 'اكتملت المسودة!' : 'Draft complete!',
              };
              setTypingLabel(stageLabels[job.stage] || (uiLanguage === 'ar' ? 'جاري صياغة المسودة...' : 'Generating draft...'));
            }
          }

          clearProgressTimers();
          setTypingLabel(null);
          setIsTyping(false);
          setTypingMode('question');
          firstTokenReceivedRef.current = true;
        } catch (draftError) {
          console.error('[DRAFT_JOB] Polling failed:', draftError);
          clearProgressTimers();
          setTypingLabel(null);
          setIsTyping(false);
          setTypingMode('question');
          if (draftError.message === 'Aborted') throw draftError;
          response = {
            text: uiLanguage === 'ar' ? 'تعذر إنشاء المسودة. يرجى المحاولة مرة أخرى.' : 'Draft generation failed. Please try again.',
            conversationId: response.conversationId,
            meta: { ...response.meta, draft_error: true },
          };
        }
      }

      // ── Artifact detection ─────────────────────────────────
      const fullResponseText = removeInvisibleDraftWarmupChars(response.text || '');
      let bubbleResponseText = fullResponseText;
      let bubbleMeta = response.meta || null;

      const isReviewArtifact = Boolean(response.meta?.review_scorecard);
      const inferredDraftArtifact = looksLikeLongLegalDraft(fullResponseText);
      const shouldOpenArtifactPanel = isDraftingFlow || isReviewArtifact || inferredDraftArtifact;

      if (shouldOpenArtifactPanel && fullResponseText) {
        const artifactType = isReviewArtifact ? 'review' : 'draft';
        const artifactTitle = response.meta?.artifact_title
          || (artifactType === 'review'
            ? (defaultPromptLanguage === 'ar' ? 'تقرير مراجعة المستند' : 'Document Review Report')
            : (defaultPromptLanguage === 'ar' ? 'مسودة عقد قانوني' : 'Legal Draft'));

        const artifactPayload = {
          type: artifactType,
          title: artifactTitle,
          content: fullResponseText,
          messageId: aiMessageId,
          conversationId: response.conversationId || chatConversationId,
          sourcePrompt: displayContent || effectiveContent,
          sourceLanguage: defaultPromptLanguage,
        };

        setArtifactByMessageId((prev) => ({ ...prev, [aiMessageId]: artifactPayload }));
        openArtifactPanel(artifactPayload);

        bubbleResponseText = ARTIFACT_CONFIRMATION_MESSAGES[artifactType][defaultPromptLanguage]
          || ARTIFACT_CONFIRMATION_MESSAGES[artifactType].en;
        bubbleMeta = {
          ...(response.meta || {}),
          artifact_panel: { message_id: aiMessageId, type: artifactType, title: artifactTitle },
        };
      }

      // ── Finalize message + cloud sync ──────────────────────
      setChats((prev) => prev.map((chat) => {
        if (chat.id === chatId) {
          const finalizedChat = {
            ...chat,
            messages: chat.messages.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, content: bubbleResponseText, isStreaming: false, meta: bubbleMeta || msg.meta || null }
                : msg
            ),
            conversationId: response.conversationId || chat.conversationId,
            updatedAt: new Date().toISOString(),
          };
          syncChatToCloud(finalizedChat);
          return finalizedChat;
        }
        return chat;
      }));

      if (trackQuestion) trackQuestion(displayContent, fullResponseText.length || 0);

      const responseTimeMs = Date.now() - requestStart;
      const sourcesCited = extractSourcesFromResponse(response.text || '');
      const suggestionText = extractSuggestionFromResponse(response.text || '', defaultPromptLanguage);
      const lawSystems = extractLawSystemsFromSources(sourcesCited);
      const mode = detectModeForTracking(displayContent || effectiveContent, usesDocument);
      const extractionSuccess = usesDocument
        ? (activeDocument?.text || '').replace(/\s+/g, '').length >= 100
        : null;

      trackUsageEvent('response_metadata', {
        question_text: displayContent,
        response_text: fullResponseText,
        response_length: fullResponseText.length || 0,
        response_time_ms: responseTimeMs,
        mode,
        law_system_detected: lawSystems,
        sources_cited: sourcesCited,
        had_sources: sourcesCited.length > 0,
        had_suggestion: Boolean(suggestionText),
        suggestion_text: suggestionText || null,
        document_uploaded: usesDocument,
        extraction_success: extractionSuccess,
        language: defaultPromptLanguage,
        conversation_id: response.conversationId || chatConversationId,
        response_id: aiMessageId,
        review_scorecard: response.meta?.review_scorecard || null,
        review_clauses_reviewed: response.meta?.review_scorecard?.clauses_reviewed ?? null,
        review_compliant_count: response.meta?.review_scorecard?.compliant ?? null,
        review_needs_edit_count: response.meta?.review_scorecard?.needs_edit ?? null,
        review_violations_count: response.meta?.review_scorecard?.violations ?? null,
        review_high_risk_count: response.meta?.review_scorecard?.risks_high ?? null,
        review_medium_risk_count: response.meta?.review_scorecard?.risks_medium ?? null,
        review_low_risk_count: response.meta?.review_scorecard?.risks_low ?? null,
        review_missing_clauses_count: response.meta?.review_scorecard?.missing_clauses ?? null,
      });
    } catch (error) {
      const abortReason = abortReasonRef.current;
      if (error.name === 'AbortError' || error.message === 'Aborted') {
        if (abortReason === 'timeout-delay' || abortReason === 'timeout-max') {
          const timeoutMessage = abortReason === 'timeout-delay'
            ? (DELAY_TIMEOUT_MESSAGES[uiLanguage] || DELAY_TIMEOUT_MESSAGES.en)
            : (REQUEST_TIMEOUT_MESSAGES[uiLanguage] || REQUEST_TIMEOUT_MESSAGES.en);
          setChats((prev) => prev.map((chat) => {
            if (chat.id === chatId) {
              return {
                ...chat,
                messages: chat.messages.map((msg) =>
                  msg.id === aiMessageId ? { ...msg, content: timeoutMessage, isError: true, isStreaming: false } : msg
                ),
              };
            }
            return chat;
          }));
        } else {
          // user cancel — drop the empty AI message
          setChats((prev) => prev.map((chat) => {
            if (chat.id === chatId) {
              return { ...chat, messages: chat.messages.filter((msg) => msg.id !== aiMessageId) };
            }
            return chat;
          }));
        }
      } else {
        console.error('Error sending message:', error);
        const errorContent = resolveErrorMessage(error, uiLanguage, usesDocument);
        setChats((prev) => prev.map((chat) => {
          if (chat.id === chatId) {
            return {
              ...chat,
              messages: chat.messages.map((msg) =>
                msg.id === aiMessageId ? { ...msg, content: errorContent, isError: true, isStreaming: false } : msg
              ),
            };
          }
          return chat;
        }));
      }
    } finally {
      clearProgressTimers();
      setIsTyping(false);
      setTypingLabel(null);
      setTypingMode('question');
      abortControllerRef.current = null;
      abortReasonRef.current = null;
      firstTokenReceivedRef.current = false;
    }
  }, [
    currentChatId, chats, createChatEntry, syncChatToCloud, documentContexts, trackQuestion, pendingDocument,
    scheduleProgressTimers, clearProgressTimers, resolveErrorMessage, uiLanguage, trackUsageEvent,
    streamChatAPI, pollExistingAnalysisJob, openArtifactPanel,
    setChats, setDocumentContexts, setPendingDocument, incrementMessageCount, setQuotedMessage,
    setTypingMode, setIsTyping, setTypingLabel, setAnalysisStepIndex, setArtifactByMessageId,
  ]);

  // ── Edit ────────────────────────────────────────────────────
  // Cluster G.E (Feb 2026): `regenerateArtifact` and
  // `regenerateResponse` were public-API exports with no consumers
  // anywhere in the live tree (verified via `grep -rn`). Removed in
  // favour of `useAgentChat`-driven flows. If a future cluster
  // re-introduces regenerate semantics, build on top of
  // `useAgentChat.sendMessage` directly.
  const editMessage = useCallback(async (messageId, newContent) => {
    if (!currentChat) return;
    const messageIndex = currentChat.messages.findIndex((m) => m.id === messageId);
    if (messageIndex === -1) return;

    setChats((prev) => prev.map((chat) => {
      if (chat.id === currentChatId) {
        return {
          ...chat,
          messages: chat.messages.slice(0, messageIndex),
          updatedAt: new Date().toISOString(),
        };
      }
      return chat;
    }));

    await sendMessage(newContent);
  }, [currentChat, currentChatId, sendMessage, setChats]);

  // ── Feedback ────────────────────────────────────────────────
  const setFeedback = useCallback((messageId, feedback, reason = null, customText = '') => {
    if (!currentChat) return;
    const messageIndex = currentChat.messages.findIndex((m) => m.id === messageId);
    const responseMessage = currentChat.messages[messageIndex];
    let questionText = '';
    for (let i = messageIndex - 1; i >= 0; i -= 1) {
      if (currentChat.messages[i].role === 'user') {
        questionText = currentChat.messages[i].content || '';
        break;
      }
    }

    const feedbackEntry = {
      id: `mfb_${Date.now()}`,
      messageId,
      chatId: currentChatId,
      feedbackType: feedback,
      reason,
      customText: customText || null,
      questionText: questionText || null,
      responseText: responseMessage?.content || null,
      conversationId: currentChat.conversationId || null,
      language: localStorage.getItem('preferred-language') || 'ar',
      timestamp: new Date().toISOString(),
      userId: session?.user?.id || 'anonymous',
      userEmail: session?.user?.email || null,
    };

    const existingLogs = JSON.parse(localStorage.getItem('bayenah-message-feedback') || '[]');
    const filteredLogs = existingLogs.filter((log) => log.messageId !== messageId || log.feedbackType !== feedback);
    filteredLogs.push(feedbackEntry);
    localStorage.setItem('bayenah-message-feedback', JSON.stringify(filteredLogs));

    if (feedback) {
      trackUsageEvent('feedback', {
        feedback,
        reason,
        custom_text: customText || null,
        question_text: questionText || null,
        response_text: responseMessage?.content || null,
        conversation_id: currentChat.conversationId || null,
        response_id: messageId,
        timestamp: new Date().toISOString(),
      });
    }

    setChats((prev) => prev.map((chat) => {
      if (chat.id === currentChatId) {
        const updatedChat = {
          ...chat,
          messages: chat.messages.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  feedback,
                  feedbackReason: feedback === 'negative' ? reason : null,
                  feedbackText: feedback === 'negative' ? customText : null,
                }
              : m
          ),
        };
        syncChatToCloud(updatedChat);
        return updatedChat;
      }
      return chat;
    }));
  }, [currentChat, currentChatId, session, trackUsageEvent, syncChatToCloud, setChats]);

  // ── Party / doc-type selection ──────────────────────────────
  const selectReviewParty = useCallback((partyOption) => {
    const optionId = (partyOption?.id || '').trim();
    if (!optionId) return;
    const optionLabel = (partyOption?.label || optionId).trim();
    const prefix = uiLanguage === 'ar' ? 'أمثل الطرف:' : 'I represent:';
    const userFacingText = `${prefix} ${optionLabel}`;
    const apiText = `${userFacingText} [[PARTY_SELECTION:${optionId}]]`;
    sendMessage(userFacingText, null, { apiContent: apiText, displayContent: userFacingText });
  }, [sendMessage, uiLanguage]);

  const selectDocumentTypeOverride = useCallback((docTypeOption) => {
    const optionId = (docTypeOption?.id || '').trim();
    if (!optionId) return;
    const optionLabel = (docTypeOption?.label || optionId).trim();
    const prefix = uiLanguage === 'ar' ? 'تعديل نوع المستند:' : 'Update document type:';
    const userFacingText = `${prefix} ${optionLabel}`;
    const apiText = `${userFacingText} [[DOC_TYPE_OVERRIDE:${optionId}]]`;
    sendMessage(userFacingText, null, { apiContent: apiText, displayContent: userFacingText });
  }, [sendMessage, uiLanguage]);

  // ── Public API (matches original ChatContext.jsx exactly) ───
  return {
    // state
    chats,
    currentChat,
    currentChatId,
    currentDocument,
    pendingDocument,
    typingLabel,
    typingMode,
    artifactPanel,
    composerPrefill,
    isTyping,
    quotedMessage,
    totalMessageCount,
    isLoadingChats,
    reportPanel,
    analysisStepIndex,
    analysisSteps: ANALYSIS_STEPS,
    // actions
    createNewChat,
    deleteChat,
    selectChat,
    sendMessage,
    attachDocument,
    clearPendingDocument,
    openArtifactPanel,
    closeArtifactPanel,
    openArtifactFromMessage,
    requestArtifactEdit,
    consumeComposerPrefill,
    clearCurrentChat,
    clearAllChatData,
    stopGenerating,
    editMessage,
    setFeedback,
    selectReviewParty,
    selectDocumentTypeOverride,
    quoteMessage,
    clearQuote,
    renameChat,
    openReportPanel,
    closeReportPanel,
    // Cluster B (Feb 06 2026): expose the cloud-sync helper + chats
    // setter so the live tree (AgentPreviewPage) can bridge agent
    // sessions into the legacy `db.chats` sidebar list.
    syncChatToCloud,
    setChats,
  };
};
